import { endOfDay, isAfter, isBefore, isSameDay } from 'date-fns';
import { useTaskTodayControls, useUpdateTask } from 'features/tasks';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ArrowRight16,
  Calendar16,
  CalendarDeleteDate16,
  Check16,
  ClockWatch16,
  Keyboard16,
  RepeatDelete16,
  Trash16,
  X16,
} from 'shared/assets/icons';
import { Icon } from 'shared/components/ui/icon';
import {
  Colors as PopupMenuColors,
  PopupMenu,
  PopupMenuButton,
  PopupMenuList,
  PopupMenuListItem,
} from 'shared/components/ui/popup-menu';
import { useToday } from 'shared/contexts/today';
import { ScheduleEntryState } from 'shared/types/habit-schedule';
import { Task } from 'shared/types/task';
import { Timestamp } from 'shared/types/timestamp';
import {
  deletePlannedOccurrenceForDate,
  getScheduleByDate,
  getScheduleEndDate,
} from 'shared/utils/schedules';

import { useData } from '../../use-data';
import * as Styled from './upcoming-slot.style';

export type ActionMenuProps = Pick<
  React.ComponentProps<typeof PopupMenu>,
  'referenceElement' | 'onOutside'
> & {
  entryBased?: boolean;
  task: Task;
  date: Timestamp;
  timeLabel: string;
  onDate: () => void;
  onTime: () => void;
  isGeneratedFrequencyEntry?: boolean;
  isFrequencyRow?: boolean;
};

export const ActionMenu: React.FC<ActionMenuProps> = ({
  entryBased,
  task,
  date,
  timeLabel,
  onDate,
  onTime,
  isGeneratedFrequencyEntry,
  isFrequencyRow,
  referenceElement,
  onOutside,
}) => {
  const { t } = useTranslation();
  const today = useToday();
  const { weekStartsOn } = useData();

  const { onComplete: onCompleteTask, openManualEntryLogging } =
    useTaskTodayControls();
  const { submit: updateTask } = useUpdateTask();

  const onStatusChange = (status: ScheduleEntryState) =>
    onCompleteTask(task, status, {
      referenceDate: date,
      toggleDayEntry: false,
      keepDetailOpen: true,
    });

  const onSkip = () => onStatusChange(ScheduleEntryState.Skip);
  const onFail = () => onStatusChange(ScheduleEntryState.Failure);
  const onComplete = () => onStatusChange(ScheduleEntryState.Complete);
  const onLogProgress = () => {
    openManualEntryLogging(task.id, date);
    onOutside?.();
  };
  const onClearProgress = () => {
    onCompleteTask(task, ScheduleEntryState.Complete, {
      value: 0,
      referenceDate: date,
      keepDetailOpen: true,
    });
    onOutside?.();
  };

  const onDeletePlannedOccurrence = () => {
    const schedules = task.schedules
      ?.toSorted(
        (scheduleA, scheduleB) =>
          scheduleA.startDate.getTime() - scheduleB.startDate.getTime(),
      )
      .map((schedule, index, arr) => {
        // get the previous schedule end date (if there is a previous schedule)
        const prevSchedule = arr[index - 1];
        const prevScheduleEndDate = prevSchedule
          ? getScheduleEndDate(prevSchedule)
          : undefined;
        const scheduleEndDate = getScheduleEndDate(schedule);

        // if the date is not part of the current schedule, the schedule is good as is
        if (
          (prevScheduleEndDate && isBefore(date, prevScheduleEndDate)) ||
          (scheduleEndDate && isAfter(date, scheduleEndDate))
        ) {
          return schedule;
        }

        return deletePlannedOccurrenceForDate(schedule, date, {
          weekStartsOn,
          today,
        });
      });
    updateTask({ id: task.id, schedules });
  };

  const activeSchedule = useMemo(
    () => getScheduleByDate(task.schedules, { date, today }),
    [task.schedules, date, today],
  );

  const hasProgress = useMemo(
    () =>
      !!activeSchedule?.entries.some(
        (e) =>
          isSameDay(e.date, date) &&
          (!!e.value ||
            e.state === ScheduleEntryState.Skip ||
            e.state === ScheduleEntryState.Failure),
      ),
    [activeSchedule?.entries, date],
  );

  return (
    <PopupMenu referenceElement={referenceElement} onOutside={onOutside}>
      {/* change date/time */}
      {!entryBased && !isGeneratedFrequencyEntry && (
        <PopupMenuList hasBottomBorder>
          <PopupMenuListItem>
            <PopupMenuButton
              onClick={onDate}
              start={<Icon icon={Calendar16} />}
            >
              {t('context-menus.task.labels.reschedule')}
            </PopupMenuButton>
            <PopupMenuButton
              onClick={onTime}
              start={<Icon icon={ClockWatch16} />}
              end={<Styled.DateLabel>{timeLabel}</Styled.DateLabel>}
            >
              {t('context-menus.task.labels.time')}
            </PopupMenuButton>
          </PopupMenuListItem>
        </PopupMenuList>
      )}
      {/* complete/skip/fail */}
      {!isFrequencyRow && !isAfter(date, endOfDay(today)) && (
        <PopupMenuList hasBottomBorder>
          <PopupMenuListItem>
            <PopupMenuButton
              onClick={onComplete}
              start={<Icon icon={Check16} />}
            >
              {t('context-menus.task.labels.complete')}
            </PopupMenuButton>
          </PopupMenuListItem>
          <PopupMenuListItem>
            <PopupMenuButton
              onClick={onSkip}
              start={<Icon icon={ArrowRight16} />}
            >
              {t('context-menus.task.labels.skip')}
            </PopupMenuButton>
          </PopupMenuListItem>
          <PopupMenuListItem>
            <PopupMenuButton onClick={onFail} start={<Icon icon={X16} />}>
              {t('context-menus.task.labels.fail')}
            </PopupMenuButton>
          </PopupMenuListItem>
          <PopupMenuListItem>
            <PopupMenuButton
              onClick={onLogProgress}
              start={<Icon icon={Keyboard16} />}
            >
              {t('context-menus.task.labels.log-progress')}
            </PopupMenuButton>
          </PopupMenuListItem>
        </PopupMenuList>
      )}
      {/* clear progress */}
      {!isFrequencyRow && hasProgress && (
        <PopupMenuList hasBottomBorder>
          <PopupMenuListItem>
            <PopupMenuButton
              onClick={onClearProgress}
              start={<Icon icon={Trash16} />}
            >
              {t('context-menus.task.labels.clear-progress')}
            </PopupMenuButton>
          </PopupMenuListItem>
        </PopupMenuList>
      )}
      {/* delete planning for day */}
      {!entryBased && !isGeneratedFrequencyEntry && (
        <PopupMenuList>
          <PopupMenuListItem>
            <PopupMenuButton
              onClick={onDeletePlannedOccurrence}
              start={<Icon icon={CalendarDeleteDate16} />}
              textColor={PopupMenuColors.Error}
              startColor={PopupMenuColors.Error}
            >
              {t('context-menus.task.labels.delete-occurrence')}
            </PopupMenuButton>
          </PopupMenuListItem>
        </PopupMenuList>
      )}

      {isFrequencyRow && (
        <PopupMenuList>
          <PopupMenuListItem>
            <PopupMenuButton
              onClick={onDeletePlannedOccurrence}
              start={<Icon icon={RepeatDelete16} />}
              textColor={PopupMenuColors.Error}
              startColor={PopupMenuColors.Error}
            >
              {t('context-menus.task.labels.dont-repeat-until-completed')}
            </PopupMenuButton>
          </PopupMenuListItem>
        </PopupMenuList>
      )}
    </PopupMenu>
  );
};
